import type { ChangeEvent } from 'react';
import { Optional } from '../../Shared';

// Providing the option to give a type here allows us to
// ensure that only valid strings are provided as options
type SelectProps<T extends string> = {
  readonly onChange?: (event: ChangeEvent<HTMLSelectElement>) => void;
  readonly labeller?: (value: T) => string;
  readonly value?: T;
  readonly showEmptyOption?: boolean;
  readonly options: readonly T[];
  readonly title: string;
  readonly className?: string;
  readonly id: string;
  readonly disabled?: boolean;
  readonly style?: React.CSSProperties;
};

/**
 * An accessible select component
 *
 * This custom component has been created for simplicity to comply with accessiblity requirements
 * since standard AntD Select components do not provide aria attributes that conform with acceptable values
 * To see how to force the standard AntD Select component to comply, see the `SearchUserSelect` component.
 *
 * Takes a labeller function to create a label for each of the provided values.
 * The value of each option is the option itself, and the label is the string returned by the labeller function.
 * If no labeller function is provided, the label of the option will be its value.
 */
const Select = <T extends string>({
  onChange,
  labeller = (o) => o,
  className,
  value,
  options,
  showEmptyOption = false,
  title,
  id,
  disabled,
  style,
}: SelectProps<T>) => {
  return (
    <select
      id={id}
      className={className}
      onChange={onChange}
      value={value}
      disabled={disabled === true}
      style={{
        width: '100%',
        height: '100%',
        border: '1px solid lightgrey',
        backgroundColor: 'white',
        paddingLeft: '7px',
        ...style,
      }}
      aria-label={`${title} Select`}
    >
      <Optional show={showEmptyOption}>
        <option value={''} style={{ color: 'lightgrey' }}></option>
      </Optional>

      {options.map((option, index) => (
        <option key={index} value={option} label={labeller(option)} />
      ))}
    </select>
  );
};

export default Select;
